import React, { useRef, useState } from 'react';
import './PlateCrossReferenceReport.css';
import inspectionService from '../../services/inspectionService';
import { useSpinner } from '../../context/SpinnerContext';
import logger from '../../utils/logger';

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        const result = reader.result;
        if (typeof result !== 'string') {
            reject(new Error('No se pudo leer el archivo'));
            return;
        }
        resolve(result.split(',')[1]);
    };
    reader.onerror = () => reject(new Error('Error al leer el archivo'));
    reader.readAsDataURL(file);
});

const PlateCrossReferenceReport = ({ onNotify }) => {
    const { showSpinner, hideSpinner } = useSpinner();
    const fileInputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);

    const handleFileSelection = (file) => {
        if (!file) return;

        const lowerName = file.name.toLowerCase();
        if (!lowerName.endsWith('.xlsx') && !lowerName.endsWith('.xls')) {
            onNotify('Solo se permiten archivos Excel (.xls o .xlsx)', 'error');
            return;
        }

        if (file.size > MAX_FILE_SIZE_BYTES) {
            onNotify('El archivo excede el tamaño máximo permitido de 5 MB', 'error');
            return;
        }

        setSelectedFile(file);
    };

    const handleProcessPlates = async (event) => {
        event.preventDefault();

        if (!selectedFile) {
            onNotify('Por favor selecciona un archivo Excel', 'error');
            return;
        }

        try {
            showSpinner('Procesando placas...');
            logger.info('Procesando cruce de placas', { fileName: selectedFile.name }, 'PlateCrossReferenceReport');

            const fileContent = await fileToBase64(selectedFile);
            const response = await inspectionService.crossReferencePlatesReport(fileContent, selectedFile.name);

            if (response.downloadUrl) {
                const link = document.createElement('a');
                link.href = response.downloadUrl;
                link.download = response.fileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                const summary = response.summary || {};
                onNotify(
                    `Reporte generado. ${summary.inputPlatesCount || 0} placas procesadas, ` +
                    `${summary.platesFound || 0} encontradas, ${summary.platesNotFound || 0} sin registro ` +
                    `(últimos ${summary.searchWindowDays || 45} días).`,
                    'success'
                );
            } else {
                onNotify('Error: No se recibió URL de descarga', 'error');
            }
        } catch (error) {
            logger.error('Error processing plate cross reference:', error, null, 'PlateCrossReferenceReport');
            onNotify(error.message || 'Error al procesar las placas', 'error');
        } finally {
            hideSpinner();
        }
    };

    return (
        <section className="report-card plate-cross-reference-report">
            <h2 className="report-card-title">Validación de Placas</h2>
            <p className="report-card-subtitle">
                Cruza un listado de placas del cliente con las inspecciones registradas
            </p>

            <form onSubmit={handleProcessPlates} className="plate-cross-reference-form">
                <div
                    className={`plate-file-drop-zone ${isDragOver ? 'dragover' : ''}`}
                    onDrop={(event) => {
                        event.preventDefault();
                        setIsDragOver(false);
                        handleFileSelection(event.dataTransfer.files?.[0]);
                    }}
                    onDragOver={(event) => {
                        event.preventDefault();
                        setIsDragOver(true);
                    }}
                    onDragLeave={() => setIsDragOver(false)}
                    onClick={() => fileInputRef.current?.click()}
                    role="button"
                    tabIndex={0}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xls,.xlsx"
                        onChange={(event) => handleFileSelection(event.target.files?.[0])}
                        className="plate-file-input"
                    />
                    <span className="plate-drop-zone-icon">📁</span>
                    <span className="plate-drop-zone-text">
                        Arrastra tu archivo Excel aquí o haz clic para seleccionar
                    </span>
                </div>

                {selectedFile && (
                    <div className="plate-selected-file">
                        <div className="plate-selected-file-info">
                            <span className="plate-file-name">{selectedFile.name}</span>
                            <span className="plate-file-size">
                                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                        </div>
                        <button type="button" className="plate-remove-file-btn" onClick={() => setSelectedFile(null)}>
                            ×
                        </button>
                    </div>
                )}

                <div className="form-help-text">
                    <p>• La columna A debe llamarse <strong>placa</strong> o <strong>placas</strong> en la celda A1</p>
                    <p>• Formatos permitidos: .xlsx o .xls</p>
                    <p>• Desde Google Sheets: Archivo → Descargar → Microsoft Excel</p>
                    <p>• La búsqueda considera inspecciones de los últimos 45 días</p>
                    <p>• El reporte se descargará automáticamente al procesarse</p>
                </div>

                <div className="report-card-actions">
                    <button
                        type="submit"
                        className="btn-generate-report btn-generate-report-primary"
                        disabled={!selectedFile}
                    >
                        Procesar placas
                    </button>
                </div>
            </form>
        </section>
    );
};

export default PlateCrossReferenceReport;
